/* billingTerms.js -- SUBSCRIPTION BILLING TERMS: THE ENGINE -- ONE OWNER (billing terms P2, 2026-10-26).
 *
 * Design: docs/BILLING-TERMS.md (Brian's decisions (a)-(l), 10-21; (m)-(p), 10-26) and diffs/billing-terms-investigation.txt
 * Q7 (the sketch) with Q-B1 / Q-B2 / Q-B4 / Q-B8 applied; build record diffs/billing-terms-p2-build.txt.
 *
 * WHAT IT DOES: an annual contract sold in month m with annual contract value (ACV) X is BILLED X / 1, X / 2, X / 4 or X / 12 on its
 * term's cycle (annual 12 / semi-annual 6 / quarterly 3 / monthly 1 months). REVENUE IS UNCHANGED (f): every term recognises ACV / 12
 * a month, so the revenue formula is today's existing-book formula. The term mix changes BILLINGS -- cash and deferred revenue.
 *
 * THE RULES (one place each):
 *   the stored shapes, the off forms, the link eligibility ......... utils/billingTermsRules.js (P1; re-used here, never re-spelled)
 *   a renewal (auto / manual, churn, adjustments, forecast-sold) .... drRecognition.computeRenewalLookback (the SHARED primitive, run
 *                                                                     once per term -- the same rule the AE and CQ engines use)
 *   everything below (the mix split, the per-term roll, the billing, the collections roll, revenue, deferred revenue) ... THIS FILE
 *
 * termsActive(settings) is FALSE when all four columns are NULL or in their off forms -- computeAEQuotaDR then runs TODAY'S code
 * path untouched (identical by construction). Pure. Plain ASCII on purpose (CLAUDE.md 12.5 rule 55). */
import { computeRenewalLookback, recognizeForwardCohort } from './drRecognition';
import { addMonths } from './dateUtils';
import { TERMS as RULE_TERMS, TERM_KEYS, RENEWAL_MODES, billingTermsAbsent, listAbsent, unbilledArLeafKeysFromMeta } from '../utils/billingTermsRules';

const r2 = n => Math.round(n * 100) / 100;
const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const num = v => { const n = typeof v === 'number' ? v : parseFloat(v); return Number.isFinite(n) ? n : 0; };

export const TERMS = RULE_TERMS;   // annual 12 / semi_annual 6 / quarterly 3 / monthly 1 -- utils/billingTermsRules.js owns it
export const UNBILLED_AR_REF = 'unbilled_ar';

/* Is any billing-terms value stored (beyond the off forms)? FALSE -> today's engine path, untouched. */
export function termsActive(settings) {
  const s = settings || {};
  const link = s.unbilled_ar_bs_line_key;
  return !(billingTermsAbsent(s.billing_terms) && listAbsent(s.renewal_adjustments) && listAbsent(s.unbilled_collections)
    && (link === undefined || link === null || link === ''));
}

/* The per-term configuration, globals as the fallback (d) (m). A value the P1 rules would refuse is read defensively here (an
 * unknown term is ignored, a bad number reads 0) -- the pre-flight's P25 names it; the engine never throws on it. */
export function resolveTermConfig(settings) {
  const s = settings || {};
  const bt = isPlainObject(s.billing_terms) ? s.billing_terms : {};
  const rawMix = isPlainObject(bt.mix) && Object.keys(bt.mix).length ? bt.mix : { annual: 1 };
  const churnOv = isPlainObject(bt.churn) ? bt.churn : {};
  const modeOv = isPlainObject(bt.renewal_mode) ? bt.renewal_mode : {};
  const typedOv = isPlainObject(bt.manual_renewals) ? bt.manual_renewals : {};
  const book = isPlainObject(bt.existing_book) ? bt.existing_book : {};
  const globalChurn = parseFloat(s.churn_rate) || 0;
  const globalMode = s.renewal_mode || 'auto';
  const out = { mix: {}, churn: {}, mode: {}, typed: {}, book: {} };
  for (const t of TERM_KEYS) {
    const sh = rawMix[t];
    out.mix[t] = (typeof sh === 'number' && Number.isFinite(sh) && sh >= 0) ? sh : 0;
    const c = churnOv[t];
    out.churn[t] = (typeof c === 'number' && Number.isFinite(c)) ? c : globalChurn;
    out.mode[t] = RENEWAL_MODES.includes(modeOv[t]) ? modeOv[t] : globalMode;
    if (t === 'annual') {
      out.typed[t] = isPlainObject(s.manual_renewals) ? s.manual_renewals : {};
      const etacv = isPlainObject(s.existing_total_acv) ? s.existing_total_acv : {};
      out.book[t] = { billings: etacv, anniversary: etacv };   // annual: the billing IS the anniversary value
    } else {
      out.typed[t] = isPlainObject(typedOv[t]) ? typedOv[t] : {};
      const b = isPlainObject(book[t]) ? book[t] : {};
      out.book[t] = { billings: isPlainObject(b.billings) ? b.billings : {}, anniversary: isPlainObject(b.anniversary) ? b.anniversary : {} };
    }
  }
  return out;
}

/* Q-B8: the month's new ACV split by the mix. Non-annual shares are rounded to the cent; ANNUAL takes the remainder (if annual's
 * share is 0, the largest share takes it -- ties go to the first term in TERMS order), so the parts always total the new ACV to the
 * cent and revenue is unchanged by the split. */
export function splitNewACV(total, mix) {
  const t0 = r2(total || 0);
  let rem = 'annual';
  if (!(mix.annual > 0)) {
    let best = -1;
    for (const t of TERM_KEYS) if ((mix[t] || 0) > best) { best = mix[t] || 0; rem = t; }
    if (!(best > 0)) rem = 'annual';
  }
  const out = {};
  let used = 0;
  for (const t of TERM_KEYS) {
    if (t === rem) continue;
    out[t] = r2(t0 * (mix[t] || 0));
    used = r2(used + out[t]);
  }
  out[rem] = r2(t0 - used);
  return out;
}

/* THE ENGINE. Same arguments as computeAEQuotaDR plus the linked Unbilled-AR line's close balance. */
export function computeBillingTermsDR(monthlyNewACV, settings, forecastMonths, closeMonth, closeUnbilledAR) {
  const s = settings || {};
  const cfg = resolveTermConfig(s);
  const startMode = s.start_mode || 'zero';
  const existing = startMode === 'existing';
  const months = (forecastMonths || []).filter(m => m > closeMonth);
  const closeUAR = r2(num(closeUnbilledAR));

  // the month's total new ACV (all reps, after scenario attainment -- (c)), and its split
  const newACV = {};
  const split = {};
  for (const m of months) { newACV[m] = r2(num((monthlyNewACV || {})[m])); split[m] = splitNewACV(newACV[m], cfg.mix); }

  // adjustments (n): summed per (term, month); only the forecast window is read (P25 names any outside it)
  const adjOf = {};
  for (const a of (Array.isArray(s.renewal_adjustments) ? s.renewal_adjustments : [])) {
    if (!isPlainObject(a) || !TERM_KEYS.includes(a.term) || typeof a.amount !== 'number' || !Number.isFinite(a.amount)) continue;
    const k = a.term + '|' + a.month;
    adjOf[k] = r2((adjOf[k] || 0) + a.amount);
  }

  // ---- the per-term roll ----
  const byTerm = {};
  for (const t of TERM_KEYS) {
    const c = (TERMS.find(x => x.key === t) || {}).cycle;
    const book = cfg.book[t];
    const A = {}, F = {}, B = {}, newT = {};
    const T = { gross: {}, churnACV: {}, billings: B, anniversary: A, forecastSoldRenewals: {}, typed: {}, newACV: newT, adjustments: {} };
    for (const m of months) newT[m] = split[m][t] || 0;
    for (const m of months) {
      const adj = adjOf[t + '|' + m] || 0;
      const r = computeRenewalLookback({ month: m, renewalMode: cfg.mode[t], manualRenewals: cfg.typed[t], existingTotalACV: book.anniversary,
        totalACV: A, churnRate: cfg.churn[t], newACV: newT, closeMonth, startMode, forecastSold: F, adjustment: adj });
      A[m] = r.totalACV;                 // the annual value whose anniversary falls in m (gross - churn + new)
      F[m] = r.forecastSoldValue;        // the forecast-sold part of it (Q-B2)
      T.gross[m] = r.grossRenewals;
      T.churnACV[m] = r.churn;
      T.forecastSoldRenewals[m] = r.forecastSoldRenewals;
      T.typed[m] = cfg.mode[t] === 'manual' ? r2(num(cfg.typed[t][m])) : 0;
      T.adjustments[m] = adj;
      if (t === 'annual') {
        B[m] = A[m];                     // annual: the bill IS the annual value (today's totalACV)
      } else {
        // the value that renewed at this anniversary a year ago, and the bill one cycle ago (history from the typed book)
        const lb = addMonths(m, -12), pb = addMonths(m, -c);
        const prevAnniv = lb <= closeMonth ? (existing ? r2(num(book.anniversary[lb])) : 0) : (A[lb] !== undefined ? A[lb] : 0);
        const prevBill = pb <= closeMonth ? (existing ? r2(num(book.billings[pb])) : 0) : (B[pb] !== undefined ? B[pb] : 0);
        // the bill = last cycle's bill + the change in annual value at this anniversary, in bill units (x c / 12): the renewal
        // change (an adjustment, or a typed renewal against last year's value) - churn + new contracts of this term
        B[m] = r2(prevBill + r2((r.grossRenewals - prevAnniv) * c / 12) - r2(r.churn * c / 12) + r2(newT[m] * c / 12));
      }
    }
    byTerm[t] = T;
  }

  // ---- collections (o): run against the CLOSE-MONTH Unbilled AR only (collectionsRoll, below -- the page reads the same rule) ----
  const { collectionsCash, collectionsCleared, unbilledAR } = collectionsRoll(s.unbilled_collections, months, closeUAR);

  // ---- totals, revenue (today's formula, (f)), deferred revenue ((g), anchored on the close) ----
  const out = { newACV: {}, grossRenewals: {}, churn: {}, netRenewals: {}, totalACV: {}, gaapRevenue: {}, deferredBalance: {}, cashReceived: {},
    schedule: [], byTerm, unbilledAR, collectionsCash, collectionsCleared, deferredInflow: {} };
  const rawRunRate = (s.existing_monthly_rev || {})[closeMonth];
  const flat = existing && rawRunRate != null && rawRunRate !== '' && !Number.isNaN(parseFloat(rawRunRate)) ? r2(parseFloat(rawRunRate) || 0) : 0;
  let cumNew = 0, cumChurn = 0;
  let bal = existing ? (parseFloat(s.opening_bs_balance) || 0) : 0;
  const display = {}; for (const m of months) display[m] = 0;
  for (const m of months) {
    let gross = 0, churn = 0, net = 0, bills = 0;
    for (const t of TERM_KEYS) {
      gross = r2(gross + byTerm[t].gross[m]);
      churn = r2(churn + byTerm[t].churnACV[m]);
      net = r2(net + r2(byTerm[t].gross[m] - byTerm[t].churnACV[m]));
      bills = r2(bills + byTerm[t].billings[m]);
    }
    cumNew = r2(cumNew + newACV[m]);
    cumChurn = r2(cumChurn + churn);
    let rev = r2(0 + r2(cumNew / 12));
    rev = r2(rev + flat);
    rev = r2(rev - r2(cumChurn / 12));
    const inflow = r2(bills + r2(collectionsCash[m] - collectionsCleared[m]));
    bal = r2(bal + inflow - rev);
    out.newACV[m] = newACV[m];
    out.grossRenewals[m] = gross;
    out.churn[m] = r2(-churn);           // NEGATIVE, annual contract value (the KPI ARR roll reads it in ACV units)
    out.netRenewals[m] = net;
    out.totalACV[m] = bills;             // = the month's billings, every term
    out.gaapRevenue[m] = rev;
    out.deferredBalance[m] = bal;
    out.cashReceived[m] = r2(bills + collectionsCash[m]);
    out.deferredInflow[m] = inflow;
    if (newACV[m] !== 0) {               // the display cohort schedule (the DR page renders it), as today's existing mode builds it
      const { monthlyRecognition, recognized, remaining } = recognizeForwardCohort(newACV[m], m, display);
      out.schedule.push({ saleMonth: m, acv: newACV[m], monthlyRecognition, recognized, remaining });
    }
  }
  // a caller month on or before the close carries 0 in every series, as today's engine writes it
  for (const m of (forecastMonths || [])) {
    if (m > closeMonth) continue;
    for (const k of ['newACV', 'grossRenewals', 'churn', 'netRenewals', 'totalACV', 'gaapRevenue', 'deferredBalance', 'cashReceived']) out[k][m] = 0;
  }
  return out;
}

/* THE COLLECTIONS ROLL (o, Q-B6) -- ONE owner: the engine above and the Deferred Revenue page (the "cleared (automatic)" figure and
 * the remaining balance after each row). Collections run against the CLOSE-MONTH Unbilled AR only, in month order and, within a
 * month, in stored order; a blank "cleared" clears min(cash, what is left of the close balance); an explicit one is used as stored.
 * Only forecast months are read. Returns the monthly series and, per stored row (by its index), what it clears and what is left. */
export function collectionsRoll(collections, months, closeUnbilledAR) {
  const closeUAR = r2(num(closeUnbilledAR));
  const list = Array.isArray(collections) ? collections : [];
  const collectionsCash = {}, collectionsCleared = {}, unbilledAR = {};
  const rows = list.map(() => null);
  for (const m of months) { collectionsCash[m] = 0; collectionsCleared[m] = 0; }
  let clearedSoFar = 0;
  for (const m of months) {
    for (let i = 0; i < list.length; i++) {
      const x = list[i];
      if (!isPlainObject(x) || x.month !== m) continue;
      const cash = r2(num(x.cash_received));
      const def = Math.min(cash, Math.max(0, r2(closeUAR - clearedSoFar)));   // the default: no more than what is left
      const explicit = typeof x.cleared === 'number' && Number.isFinite(x.cleared);
      const cl = explicit ? r2(x.cleared) : r2(def);
      collectionsCash[m] = r2(collectionsCash[m] + cash);
      collectionsCleared[m] = r2(collectionsCleared[m] + cl);
      clearedSoFar = r2(clearedSoFar + cl);
      rows[i] = { cleared: cl, automatic: !explicit, remaining: r2(closeUAR - clearedSoFar) };
    }
  }
  let uar = closeUAR;
  for (const m of months) { uar = r2(uar - collectionsCleared[m]); unbilledAR[m] = uar; }
  return { collectionsCash, collectionsCleared, unbilledAR, rows, totalCleared: r2(clearedSoFar), closeUAR };
}

/* ---- THE UNBILLED-AR LINK (p) -- the accrued-payroll link pattern (engines/accruedPayroll.js) ----
 *   buildBSLines wires the linked line to the series ......... unbilledArLinkKey   (the rule)
 *   the orchestrator serves the series where it is wired ...... unbilledArBuiltKey  (asked of the BUILT skeleton)
 *   computeBS ........................................................ UNCHANGED -- its module_link arm reads IS_REF['unbilled_ar']
 * The link is "active" exactly when runFullModel runs the AE module (a real row, not disabled) -- ONE predicate for runFullModel,
 * ModelProvider and the pre-flight. */
export function unbilledArLinkFromSettings(aeqSettings) {
  const s = aeqSettings || {};
  const key = s.unbilled_ar_bs_line_key;
  return { active: (s._row_exists !== false) && (s.enabled !== false), lineKey: (typeof key === 'string' && key) ? key : null };
}
export function unbilledArLinkKey(link, bsActualsMeta, taken) {
  if (!link || !link.active || !link.lineKey) return null;
  if (!unbilledArLeafKeysFromMeta(bsActualsMeta).includes(link.lineKey)) return null;
  if (taken && Object.prototype.hasOwnProperty.call(taken, link.lineKey)) return null;
  return link.lineKey;
}
export function unbilledArBuiltKey(bsLines) {
  const l = (bsLines || []).find(x => x && x.module_link_ref === UNBILLED_AR_REF);
  return l ? l.id : null;
}
